import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Plus, Trash2, UserRound, Phone, Mail } from "lucide-react";

export function ContactsTab() {
  const [contacts, setContacts] = useState([]);
  const [name, setName] = useState("");
  const [roleLabel, setRoleLabel] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/contacts");
    setContacts(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) { toast.error("Geef een naam op"); return; }
    setSaving(true);
    try {
      await api.post("/contacts", { name: name.trim(), role_label: roleLabel.trim(), phone: phone.trim(), email: email.trim() });
      setName(""); setRoleLabel(""); setPhone(""); setEmail("");
      toast.success("Contact toegevoegd");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/contacts/${id}`); toast.success("Contact verwijderd"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="contacts-tab">
      <div className="mb-1 flex items-center gap-2 text-sm font-bold text-slate-900"><UserRound className="h-4 w-4 text-blue-600" /> Contacten</div>
      <p className="mb-4 text-xs text-slate-500">Bijv. bevelvoerders die ter plaatse zijn. Deze verschijnen in de keuzelijst bij "Live meekijken" op een incident.</p>

      <div className="mb-5 grid gap-2 rounded-xl bg-slate-50 p-3 sm:grid-cols-5">
        <input data-testid="contact-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Naam"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
        <input data-testid="contact-role-input" value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)} placeholder="Functie (bijv. Bevelvoerder)"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
        <input data-testid="contact-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefoon"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
        <input data-testid="contact-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
        <button data-testid="contact-add-btn" onClick={add} disabled={saving}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">
          <Plus className="h-4 w-4" /> Toevoegen
        </button>
      </div>

      {contacts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">Nog geen contacten</div>
      ) : (
        <div className="space-y-2">
          {contacts.map((c) => (
            <div key={c.id} data-testid={`contact-row-${c.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-900">{c.name}</span>
                  {c.role_label && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-600">{c.role_label}</span>}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-slate-500">
                  {c.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</span>}
                  {c.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</span>}
                </div>
              </div>
              <button data-testid={`contact-delete-${c.id}`} onClick={() => del(c.id)} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
